import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  model,
  OnDestroy,
  signal,
  ViewChild,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { AbbSidePanelComponent } from '@components/abb-side-panel/abb-side-panel.component';
import {
  assetListByTypeQuery,
  createGroupMutation,
  updateGroupMutation,
} from '@graphql/asset-management';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { Apollo } from 'apollo-angular';
import {
  AssetListByTypeQuery,
  AssetListByTypeQueryVariables,
  CreateGroupMutation,
  CreateGroupMutationVariables,
  GroupListQuery,
  SaveAssetType,
  UpdateGroupMutation,
  UpdateGroupMutationVariables,
} from 'gql/graphql';
import { Subscription } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';
import {
  SIDE_PANEL_DATA,
  SidePanelData,
} from '@components/abb-side-panel/side-panel';
import { Asset, assetGroups, Device, Property } from '@constants/assets';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';

export type GroupsSidePanelData =
  | { mode: 'new' }
  | { mode: 'edit'; defaultData: GroupListQuery['groupList'][number] };

@Component({
  selector: 'abb-groups-side-panel',
  standalone: true,
  imports: [
    AbbSidePanelComponent,
    TPipe,
    AbbButtonComponent,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    AbbIconComponent,
  ],
  template: `
    <abb-side-panel
      id="new-group"
      [title]="'New Group' | t"
      #panelRef
      [backdropClickClosable]="!saving()"
      [closable]="!saving()"
    >
      <form class="flex flex-col flex-1 min-h-0" (ngSubmit)="save()">
        <div class="flex flex-col flex-1 overflow-auto">
          <mat-form-field>
            <mat-label>{{ 'Name' | t }}</mat-label>
            <input
              matInput
              [(ngModel)]="name"
              id="group-name"
              name="group-name"
              required
            />
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ 'Assets' | t }}</mat-label>
            <mat-chip-grid #chipGrid>
              <mat-chip-row
                *ngFor="let asset of selectedAssets(); trackBy: trackAsset"
                [id]="asset.name"
                (removed)="removeAsset(asset)"
                class="chips"
              >
                {{ asset.name }}
                <button id="remove-asset-btn" matChipRemove type="button">
                  <abb-icon size="small" name="mat-outlined/close"></abb-icon>
                </button>
              </mat-chip-row>
            </mat-chip-grid>
            <input
              type="text"
              matInput
              placeholder="{{ 'Search' | t }}"
              [(ngModel)]="assetQuery"
              name="asset-query"
              [matAutocomplete]="auto"
              #assetsAutocompleteInputRef
              (keydown.enter)="$event.preventDefault()"
              (keydown.tab)="$event.preventDefault()"
              [matChipInputFor]="chipGrid"
              [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
              (focusout)="resetAssetSearch()"
            />
            <mat-autocomplete
              #auto="matAutocomplete"
              (optionSelected)="selectAsset($event); assetQuery.set('')"
            >
              <mat-option
                *ngFor="let device of filteredDeviceAssets; trackBy: trackAsset"
                [value]="device"
                (mousedown)="isSelectingAsset = true"
              >
                {{ device.name }}
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ 'Description' | t }}</mat-label>
            <textarea
              matInput
              [(ngModel)]="description"
              id="group-description"
              name="group-description"
              rows="4"
            ></textarea>
          </mat-form-field>
          <h6>{{ 'Preview' | t }}</h6>
          <div class="flex flex-col gap-3">
            <div
              class="flex rounded shadow overflow-hidden items-center gap-x-2 mt-2"
            >
              <div
                class="w-12 h-12 bg-abb-black/[0.059] flex items-center justify-center"
              >
                <abb-icon
                  size="medium"
                  name="mat-outlined/image"
                  class="text-abb-font/85"
                ></abb-icon>
              </div>
              <span class="text-sm font-medium"> {{ name() }}</span>
            </div>
            @for (selectedAsset of selectedAssets(); track (selectedAsset.id +
            selectedAsset.type)) {
            <div
              class="flex rounded shadow overflow-hidden items-center gap-x-2 ml-24"
            >
              <div
                class="w-12 h-12"
                [ngStyle]="{
                  backgroundImage: 'url(' + selectedAsset.imgUrl + ')'
                }"
              ></div>
              <span class="text-sm font-medium"> {{ selectedAsset.name }}</span>
            </div>
            }
          </div>
        </div>
        <div class="w-full flex flex-row gap-8 justify-end py-8">
          <abb-button
            id="cancel"
            variant="clean"
            [disabled]="saving()"
            (onclick)="onCancel()"
            >{{ 'cancel' | t }}</abb-button
          >
          <abb-button
            id="save"
            type="submit"
            [loading]="saving()"
            [disabled]="saving()"
            >{{ (data.mode === 'new' ? 'Create' : 'Save') | t }}</abb-button
          >
        </div>
      </form>
    </abb-side-panel>
  `,
  styles: `
  `,
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'asset_management.tabs.groups' },
  ],
})
export class GroupsSidePanelComponent
  implements OnDestroy, SidePanelData<GroupsSidePanelData>
{
  panelRef = viewChild<AbbSidePanelComponent>('panelRef');
  assetsAutocompleteInputRef = viewChild<ElementRef<HTMLInputElement>>(
    'assetsAutocompleteInputRef'
  );
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger | undefined;
  name = model('');
  description = model('');
  assetQuery = model<string>('');
  saving = signal(false);
  readonly assetGroups = assetGroups;
  deviceAssets = signal<Array<Device>>([]);
  availableAssets = signal<{
    properties: Array<Property>;
    devices: Array<Device>;
  }>({
    devices: [],
    properties: [],
  });
  filteredAssets = computed(() => ({
    properties: this.availableAssets().properties.filter(
      (a) =>
        a.name.toLowerCase().includes(this.assetQuery()?.toLowerCase()) &&
        !this.selectedAssets().some(
          (sel) => sel.id === a.id && sel.type === a.type
        )
    ),
    devices: this.availableAssets().devices.filter(
      (a) =>
        a.name.toLowerCase().includes(this.assetQuery()?.toLowerCase()) &&
        !this.selectedAssets().some(
          (sel) => sel.id === a.id && sel.type === a.type
        )
    ),
  }));
  selectedAssets = signal<Array<Asset>>([]);
  separatorKeysCodes: number[] = [ENTER, COMMA];
  private subscriptions: Subscription[] = [];
  prefix = inject(TRANSLATION_PREFIX);
  data = inject<GroupsSidePanelData>(SIDE_PANEL_DATA);
  isSelectingAsset = false;

  constructor(
    private readonly apollo: Apollo,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService
  ) {
    this.subscriptions.push(
      apollo
        .watchQuery<AssetListByTypeQuery, AssetListByTypeQueryVariables>({
          query: assetListByTypeQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.deviceAssets.set(
              data.assetListByType.devices.map((d) => ({
                ...d,
                type: 'device' as const,
              }))
            );
            // this.availableAssets.set({
            //   devices: data.assetListByType.devices.map((d) => ({
            //     ...d,
            //     type: 'device' as const,
            //   })),
            //   properties: data.assetListByType.properties.map((p) => ({
            //     ...p,
            //     type: 'property' as const,
            //   })),
            // });
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toastService.notifyError(errMsg);
          },
        })
    );
    if (this.data.mode === 'edit') {
      this.name.set(this.data.defaultData.name);
      this.description.set(this.data.defaultData.description);
      this.selectedAssets.set(
        this.data.defaultData.assets.map((asset) =>
          asset.__typename === 'Device'
            ? { ...asset, type: 'device' as const }
            : { ...asset, type: 'property' as const, __typename: 'Property' }
        )
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onCancel() {
    this.panelRef()?.dispose();
  }
  autocompleteDisplayWith(asset: any) {
    return asset?.name ?? '';
  }

  removeAsset(asset: Asset) {
    this.selectedAssets.update((prev) =>
      prev.filter((a) => a.id !== asset.id || a.type !== asset.type)
    );
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim().toLowerCase();
    const match =
      this.filteredAssets().properties.find(
        (a) => a.name.toLowerCase() === value
      ) ||
      this.filteredAssets().devices.find((a) => a.name.toLowerCase() === value);
    if (match) {
      const previousValue = this.selectedAssets();
      this.selectedAssets.update((prev) => {
        if (!prev.some((a) => a.id === match.id && a.type === a.type)) {
          return [...prev, match];
        }
        return prev;
      });
      if (previousValue === this.selectedAssets()) {
        // value was already selected
        return;
      }
      event.chipInput?.clear();
      this.assetQuery.set('');
    }
  }

  selectAsset(event: MatAutocompleteSelectedEvent) {
    const asset = event.option.value;
    if (!this.selectedAssets().some((a) => a.id === asset.id)) {
      this.selectedAssets.update((prev) => [...prev, asset]);
    }
    this.assetQuery.set('');
    const inputRef = this.assetsAutocompleteInputRef();
    if (inputRef) inputRef.nativeElement.value = '';
  }
  resetAssetSearch() {
    setTimeout(() => {
      if (!this.isSelectingAsset) {
        this.assetQuery.set('');
        const inputRef = this.assetsAutocompleteInputRef();
        if (inputRef) {
          inputRef.nativeElement.value = '';
          inputRef.nativeElement.blur();
        }
        if (this.autocompleteTrigger) {
          this.autocompleteTrigger.closePanel();
        }
      }
      this.isSelectingAsset = false;
    }, 100);
  }
  trackAsset(index: number, asset: Asset) {
    return asset.id;
  }

  get filteredDeviceAssets(): Asset[] {
    const query = this.assetQuery().trim().toLowerCase();
    return this.deviceAssets().filter(
      (d) =>
        (!query || d.name.toLowerCase().includes(query)) &&
        !this.selectedAssets().some((a) => a.id === d.id)
    );
  }

  save() {
    if (!this.selectedAssets().length) {
      this.toastService.notifyError(
        t(this.prefix, `Select at least one asset`, this.translate)
      );
      return;
    }
    this.saving.set(true);
    if (this.data.mode === 'new') {
      this.apollo
        .mutate<CreateGroupMutation, CreateGroupMutationVariables>({
          mutation: createGroupMutation,
          variables: {
            assets: this.selectedAssets().map((a) => ({
              id: a.id,
              type:
                a.__typename === 'Device'
                  ? SaveAssetType.Device
                  : SaveAssetType.Property,
            })),
            description: this.description(),
            name: this.name(),
          },
        })
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.toastService.notifySuccess(
              t(
                this.prefix,
                `{name} was created successfully`,
                this.translate,
                { name: this.name() }
              )
            );
            this.panelRef()?.dispose();
            this.apollo.client.refetchQueries({
              include: ['GroupList'],
            });
          },
          error: () => {
            this.saving.set(false);
            this.toastService.notifyError(
              t(
                this.prefix,
                `An error occurred while creating {name}`,
                this.translate,
                { name: this.name() }
              )
            );
          },
        });
    } else {
      this.apollo
        .mutate<UpdateGroupMutation, UpdateGroupMutationVariables>({
          mutation: updateGroupMutation,
          variables: {
            assets: this.selectedAssets().map((a) => ({
              id: a.id,
              type:
                a.__typename === 'Device'
                  ? SaveAssetType.Device
                  : SaveAssetType.Property,
            })),
            description: this.description(),
            name: this.name(),
            id: this.data.defaultData.id,
          },
        })
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.toastService.notifySuccess(
              t(
                this.prefix,
                `{name} was updated successfully`,
                this.translate,
                { name: this.name() }
              )
            );
            this.panelRef()?.dispose();
            this.apollo.client.refetchQueries({
              include: ['GroupList'],
            });
          },
          error: () => {
            this.saving.set(false);
            this.toastService.notifyError(
              t(
                this.prefix,
                `An error occurred while updating {name}`,
                this.translate,
                { name: this.name() }
              )
            );
          },
        });
    }
  }
}
